import React, { useState } from "react";
import Panel from "components/Panel";

import Grid, { GridColumn } from "components/Grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import { QueryParameterValue, stringifyValue } from "../Workbook/util";
import { VariantPanel } from "../WorkbookVariant";

import {
  WorkbookDetailsQuery,
  WorkbookDetailsDocument,
} from "../Workbook/gql/Query.generated";
import { useDeleteWorkbookParameterSetsMutation } from "../ChooseParameters/gql/Mutation.delete.generated";
export type WorkbookType = WorkbookDetailsQuery["getWorkbookDetails"];

import ModalContainer from "components/ModalContainer";
import CreateParameterSetPanel from "../CreateParameterSetPanel";
import Callout from "components/Callout";
import Popover from "components/Popover";

const ParameterSetsPanel = ({
  databaseId,
  workbook,
}: {
  workbook: WorkbookType;
  databaseId: string;
}) => {
  const [showOnlyDiff, setShowOnlyDiff] = useState(false);
  const [deleteWorkbookParameterSets] =
    useDeleteWorkbookParameterSetsMutation();
  const [showAddParameterSet, setShowAddParameterSet] = useState(false);

  const secondaryTitle = (
    <div className="flex">
      <label>
        <input
          type="checkbox"
          checked={showOnlyDiff}
          id="show_only_diff"
          onChange={(evt) => setShowOnlyDiff(evt.target.checked)}
        />{" "}
        Show only parameters with different values
      </label>
    </div>
  );

  const paramSetsCount = workbook.parameterSets.length;
  const aliasList = workbook.parameterRefAliases;
  const data = aliasList.map((rawAlias) => {
    const alias = `$${rawAlias}`;
    const dat: Record<string, { value: QueryParameterValue; type?: string }> = {
      alias: { value: alias },
    };
    workbook.aliasParamMapList.forEach((val) => {
      const colName = `set${val.id}`;
      // val.parameters: { "$alias1": { value: "foo", type: "text" }, ... }
      dat[colName] = val.parameters[alias];
    });
    return dat;
  });
  const columns: GridColumn<
    (typeof data)[number],
    keyof (typeof data)[number]
  >[] = [
    {
      field: "alias",
      header: "",
      renderer: ({ fieldData }) => fieldData.value,
      className: "font-medium",
      width: "1fr",
      disableSort: true,
    },
  ];
  let valueWidth = "15%";
  switch (paramSetsCount) {
    case 1:
      valueWidth = "50%";
      break;
    case 2:
      valueWidth = "40%";
      break;
    case 3:
      valueWidth = "25%";
      break;
  }

  workbook.aliasParamMapList.forEach((aliasParamMap) => {
    columns.push({
      field: `set${aliasParamMap.id}`,
      header: (
        <div>
          {aliasParamMap.name}
          {workbook.aliasParamMapList.length > 1 && (
            <FontAwesomeIcon
              icon={faTrashAlt}
              title="Delete"
              className="text-[#CA1515] ml-2 cursor-pointer"
              onClick={() => {
                if (
                  window.confirm(
                    `Delete parameter set "${aliasParamMap.name}"? This will delete all EXPLAIN results for this parameter set.`,
                  )
                ) {
                  deleteWorkbookParameterSets({
                    variables: {
                      workbookId: workbook.id,
                      parameterSetId: aliasParamMap.id,
                    },
                    refetchQueries: [
                      {
                        query: WorkbookDetailsDocument,
                        variables: {
                          workbookId: workbook.id,
                          databaseId,
                        },
                      },
                    ],
                    awaitRefetchQueries: true,
                  });
                }
              }}
            />
          )}
        </div>
      ),
      renderer: function ParamValueCell({ fieldData }) {
        if (!fieldData) {
          // Avoid the race condition of trying to render this while the filteredData is not updated yet
          return null;
        }
        return (
          <span title={stringifyValue(fieldData.value, fieldData.type)}>
            {stringifyValue(fieldData.value, fieldData.type)}
          </span>
        );
      },
      width: valueWidth,
      disableSort: true,
    });
  });

  const filteredData = data.filter((dat) => {
    if (showOnlyDiff) {
      const colNames = workbook.parameterSets.map((val) => `set${val.id}`);
      const values = colNames.map((colName) => dat[colName]);
      return values.some((value) => value.value !== values[0].value);
    }
    return true;
  });
  const hasMaxParameterSetLength = workbook.parameterSets.length === 5;

  return (
    <>
      <VariantPanel workbook={workbook} variant={workbook.baselineQuery} />
      <div className="mb-4 text-right">
        <Popover
          content={
            hasMaxParameterSetLength &&
            "Only up to 5 parameter sets can be created"
          }
        >
          <button
            className="btn btn-primary"
            onClick={() => setShowAddParameterSet(true)}
            disabled={hasMaxParameterSetLength}
          >
            Add parameter set
          </button>
        </Popover>
      </div>
      <Panel title="Parameter Sets" secondaryTitle={secondaryTitle}>
        <Grid data={filteredData} columns={columns} />
      </Panel>
      {showAddParameterSet && (
        <AddParameterSetModal
          onDismiss={() => setShowAddParameterSet(false)}
          workbook={workbook}
        />
      )}
    </>
  );
};

const AddParameterSetModal = ({
  onDismiss,
  workbook,
}: {
  onDismiss: () => void;
  workbook: WorkbookType;
}) => {
  return (
    <ModalContainer
      title="Add Parameter Set"
      layout="centered"
      onClose={onDismiss}
    >
      <Callout className="mb-4">
        After adding a parameter set, you can add its missing EXPLAIN plans for
        each variant by going to each variant's page and either uploading
        EXPLAIN results manually or re-running the collector workflow.
      </Callout>
      <CreateParameterSetPanel
        workbook={workbook}
        addParamMethod="manual"
        onParameterSetsCreated={onDismiss}
      />
    </ModalContainer>
  );
};

export default ParameterSetsPanel;
